import React, { useMemo } from "react";
import { Activity, Home as HomeIcon, LogOut } from "lucide-react";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";

const DashboardHeader = ({ user, sticky = true }) => {
  const navigate = useNavigate();

  const name =
    user?.displayName || (user?.email ? user.email.split("@")[0] : "Patient");
  const firstName = name.split(" ")[0];

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric"
      }),
    []
  );

  const initials = name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(229, 231, 235, 0.5)",
        position: sticky ? "sticky" : "relative",
        top: 0,
        zIndex: 50,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
      }}
    >
      <style>{`
        @media (max-width: 640px){
          .hdr-sub { display: none; }
          .hdr-wrap { height: 60px !important; padding: 0 16px !important; }
          .hdr-greeting { font-size: 16px !important; }
        }
        .hdr-btn:hover { transform: translateY(-1px); }
      `}</style>

      <div
        className="hdr-wrap"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: 72
        }}
      >
        {/* Left: app mark + greeting */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              background: "linear-gradient(135deg, #3b82f6, #6366f1)",
              padding: 12,
              borderRadius: 16,
              boxShadow: "0 10px 15px -3px rgba(59,130,246,0.3)"
            }}
          >
            <Activity style={{ color: "white", width: 24, height: 24 }} />
          </div>
          <div>
            <h1
              className="hdr-greeting"
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 800,
                background: "linear-gradient(135deg, #111827, #6b7280)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}
            >
              {greeting}, {firstName}
            </h1>
            <p className="hdr-sub" style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
              {today} • Manage your appointments & records
            </p>
          </div>
        </div>

        {/* Right: nav + avatar + sign out */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => navigate("/patient-home")}
            className="hdr-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              background: "rgba(79, 70, 229, 0.08)",
              color: "#4F46E5",
              border: "1px solid rgba(79,70,229,0.2)",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              transition: "transform .15s ease, box-shadow .15s ease"
            }}
            title="Home"
          >
            <HomeIcon style={{ width: 18, height: 18 }} />
            <span className="hdr-sub">Home</span>
          </button>

          <div
            title={user?.displayName || user?.email}
            style={{
              width: 36,
              height: 36,
              borderRadius: "9999px",
              background: "linear-gradient(135deg,#e5e7eb,#f3f4f6)",
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
              border: "2px solid white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
            }}
          >
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || user.email}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <span style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>
                {initials}
              </span>
            )}
          </div>

          <button
            onClick={() => auth.signOut()}
            className="hdr-btn"
            style={{
              padding: 8,
              color: "#6b7280",
              border: "none",
              background: "transparent",
              borderRadius: 10,
              cursor: "pointer"
            }}
            title="Sign Out"
            aria-label="Sign out"
          >
            <LogOut style={{ width: 20, height: 20 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;
